import { CorporationType } from '@commenda/prisma';
import {
  SupportedCorporationInfo,
  SupportedEntityType,
} from '@commenda/types/src/corporation';
import { useMemo } from 'react';
// Import the module, not the package barrel: @commenda/api re-exports this file, so the barrel
// would make the two packages import each other.
import {
  useSupportedEntityTypes,
  useSupportedJurisdictions,
} from '@commenda/api/business-entity/useSupportedEntities';

// Built here rather than sent: keying the same values again by corporation type doubled a
// response already over a megabyte. Several forms can share one type, so the first in id order
// wins the slot, which is what the backend did when it built this map itself.
function byCorporationType(
  entityTypes: SupportedCorporationInfo['entityTypes']
): SupportedCorporationInfo['corporations'] {
  return Object.fromEntries(
    Object.entries(entityTypes).map(([country, byJurisdiction]) => [
      country,
      Object.fromEntries(
        Object.entries(byJurisdiction ?? {}).map(([jurisdictionId, forms]) => [
          jurisdictionId,
          Object.fromEntries(
            Object.keys(forms)
              .sort()
              .map((formId) => forms[formId])
              .filter(
                (form, index, all) =>
                  form?.corporationType &&
                  all.findIndex(
                    (other) => other?.corporationType === form.corporationType
                  ) === index
              )
              .map((form) => [form.corporationType, form])
          ) as Partial<Record<CorporationType, SupportedEntityType>>,
        ])
      ),
    ])
  );
}

// New code should read `entityTypes`, keyed by legal form id. `corporations` can only hold a type
// the legacy vocabulary named, so it still shows one form where several share a type.
export function useSupportedCorporations() {
  const { data: entityTypes, error: entityTypesError } =
    useSupportedEntityTypes();
  const { data: jurisdictions, error: jurisdictionsError } =
    useSupportedJurisdictions();

  const data: SupportedCorporationInfo | undefined = useMemo(
    () =>
      entityTypes && jurisdictions
        ? {
            jurisdictions: jurisdictions.jurisdictions,
            corporations: byCorporationType(entityTypes.entityTypes),
            entityTypes: entityTypes.entityTypes,
            ownershipVocabulary: entityTypes.ownershipVocabulary,
          }
        : undefined,
    [entityTypes, jurisdictions]
  );

  return { data, error: entityTypesError ?? jurisdictionsError };
}
